import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";

const TYPES = ["All", "Call", "Email", "Meeting", "Note", "Task"];

const TYPE_META = {
  Call: { color: "#0b72b9", bg: "#eff6ff", icon: "📞" },
  Email: { color: "#6d28d9", bg: "#f5f3ff", icon: "✉️" },
  Meeting: { color: "#b45309", bg: "#fffbeb", icon: "🤝" },
  Note: { color: "#475569", bg: "#f8fafc", icon: "📝" },
  Task: { color: "#15803d", bg: "#f0fdf4", icon: "✅" },
};

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || TYPE_META.Note;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 11,
        fontWeight: 700,
        color: meta.color,
        background: meta.bg,
        padding: "2px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      {meta.icon} {type}
    </span>
  );
}

export default function ActivitiesPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ limit: 200 });
      const res = await apiFetch(`/api/crm/activities?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setActivities(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load activities.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (typeFilter === "All") return activities;
    return activities.filter((a) => a.type === typeFilter);
  }, [activities, typeFilter]);

  const counts = useMemo(() => {
    const map = { All: activities.length };
    for (const t of TYPES.slice(1)) {
      map[t] = activities.filter((a) => a.type === t).length;
    }
    return map;
  }, [activities]);

  return (
    <div>
      <PageHeader
        title="Activities"
        subtitle="All calls, emails, meetings, and notes across your pipeline."
      />

      <div
        className="glass-card"
        style={{
          marginBottom: 16,
          padding: 14,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          overflowX: "auto",
        }}
      >
        <div
          className="crm-stage-filter"
          aria-label="Activity type filter"
          style={{ flexWrap: "nowrap", flex: 1 }}
        >
          {TYPES.map((t) => (
            <button
              key={t}
              type="button"
              className={typeFilter === t ? "active" : ""}
              onClick={() => setTypeFilter(t)}
            >
              {t}
              <span>{counts[t] ?? 0}</span>
            </button>
          ))}
        </div>
        {!loading && (
          <span style={{ fontSize: 13, color: "var(--text-2)", whiteSpace: "nowrap" }}>
            {filtered.length} activit{filtered.length !== 1 ? "ies" : "y"}
          </span>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="glass-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "24px 20px" }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Loader key={i} type="skeleton" height={56} style={{ marginBottom: 10 }} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 32 }}>
            <EmptyState
              title={typeFilter !== "All" ? `No ${typeFilter} activities yet` : "No activities yet"}
              subtitle="Log calls, emails, and meetings from the Pipeline page by clicking Activity on a deal."
            />
          </div>
        ) : (
          <div>
            {filtered.map((activity, idx) => (
              <div
                key={activity.id}
                style={{
                  display: "flex",
                  gap: 16,
                  padding: "14px 20px",
                  borderBottom: idx < filtered.length - 1 ? "1px solid var(--border)" : "none",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ paddingTop: 2, flexShrink: 0 }}>
                  <TypeBadge type={activity.type} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
                    {activity.summary}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-2)" }}>
                    {activity.accountName && (
                      <span style={{ fontWeight: 600, color: "var(--text-1)" }}>
                        {activity.accountName}
                      </span>
                    )}
                    {activity.accountName && activity.createdByUserName && " · "}
                    {activity.createdByUserName && (
                      <span>{activity.createdByUserName}</span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "var(--text-2)", whiteSpace: "nowrap", flexShrink: 0 }}>
                  {formatDate(activity.activityAt)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
